import React from "react";
import MainHeader from "../common/components/mainHeader/mainHeader";
import Menu from "../common/components/menu/menu";
import {Switch, BrowserRouter as Router, Redirect} from "react-router-dom";
import {routes} from "../helpers/routing";
import "./app.scss";
import "../styles/scrollbar.scss";
import {AuthService} from "../common/services/authService";
import {storesContext} from "../common/stores/stores";
import RouteWithSubRoutes from "../common/components/shared/routeWithSubRoutes/routeWithSubRoutes";
import i18n from "../i18n/i18n";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import {DateUtils} from "../helpers/dateUtils";
import {observer} from "mobx-react";
import {IUserStore} from "../administration/stores/userStore";
import {AlertsService} from "../alerts/services/alertsService";
import OwnedVehiclePopup from "../vehicleAssigment/components/ownedVehiclePopup/ownedVehiclePopup";
import {checkIfCookieExists} from "../helpers/cookiesUtils";
import {Utils} from "../helpers/utils";
import {alertsRoutingAddress} from "../alerts/routing/alertsRouting";
import {timetableRoutingAddress} from "../timetables/routing/timetablesRouting";
import {administrationRoutingAddress} from "../administration/routing/administrationRouting";
import {UserInManagementRoles} from "../helpers/userRoles";

interface AppState {
    displayOwnVehiclePopup: boolean;
}

class App extends React.Component<any, AppState> {
    static contextType = storesContext;
    private language = "pl";
    private countOfAlertsInterval = 5000;
    private usersInManagmentRolesMap = new Map<UserInManagementRoles, string>();

    constructor(props: any) {
        super(props);

        this.state = {
            displayOwnVehiclePopup: false
        };

        this.usersInManagmentRolesMap.set(UserInManagementRoles.administrator, administrationRoutingAddress.list);
        this.usersInManagmentRolesMap.set(UserInManagementRoles.depo, alertsRoutingAddress.list);
        this.usersInManagmentRolesMap.set(UserInManagementRoles.observer, alertsRoutingAddress.list);
        this.usersInManagmentRolesMap.set(UserInManagementRoles.supervisor, timetableRoutingAddress.week);
    }

    async componentDidMount() {
        const userStore = this.context.userStore as IUserStore;

        await this.setUserdata();
        this.setDocumentTranslate();

        if (userStore.username && userStore.displayAlerts) {
            await this.getCountOfUnreadAlerts();
            setInterval(await this.getCountOfUnreadAlerts.bind(this), this.countOfAlertsInterval);
        }

        this.setState({
            displayOwnVehiclePopup: userStore.displayOwnVehicleSelection && !checkIfCookieExists("ownVehicleActionWasPerformed")
        });
    }

    public render() {
        const userStore = this.context.userStore as IUserStore;
        const defaultPage = (userStore.group && this.usersInManagmentRolesMap.has(userStore.group)) ? this.usersInManagmentRolesMap.get(userStore.group) || "/" : "/";

        return (
            <div className={`app ${Utils.isTabletOrMobile() ? "mobile" : ""}`}>
                <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    locale={DateUtils.localeMap[this.context.userStore.language as keyof typeof DateUtils.dateMaskMap]}
                >
                    <Router>
                        <Menu/>
                        <div className="main-content">
                            <MainHeader/>
                            <div className="switch-wrapper">
                                {
                                    window.location.pathname === "/" ?
                                        <Redirect to={defaultPage}/>
                                        :
                                        <Switch>
                                            {routes.map((route, i) => (
                                                <RouteWithSubRoutes key={i} {...route} />
                                            ))}
                                        </Switch>
                                }
                            </div>
                            {this.state.displayOwnVehiclePopup && this.renderOwnedVehiclePopup()}
                        </div>
                    </Router>
                </LocalizationProvider>
            </div>
        );
    }

    private renderOwnedVehiclePopup() {
        return (
            <OwnedVehiclePopup
                displayPopup={this.state.displayOwnVehiclePopup}
                closePopup={() => {
                    this.setState({displayOwnVehiclePopup: false});
                }}
                disabledButtonOnLoadData
            />
        );
    }

    private async getCountOfUnreadAlerts() {
        const userStore = this.context.userStore as IUserStore;

        const responseCountOfAlerts = await AlertsService.getCountOfUnreadAlerts();
        userStore.setUnreadAlertsCount(responseCountOfAlerts?.data?.count);
    }

    private async setUserdata() {
        const responseAuthenticatedUser = await AuthService.authenticatedUser();
        const userStore = this.context.userStore as IUserStore;
        this.language = responseAuthenticatedUser?.language || "pl";
        await i18n.changeLanguage(this.language);
        userStore.setUsername(responseAuthenticatedUser?.username || "");
        userStore.setLanguage(this.language);
        userStore.setRoles(responseAuthenticatedUser?.roles || []);
        userStore.setGroup(responseAuthenticatedUser?.groups || []);
    }

    private setDocumentTranslate() {
        document.documentElement.lang = this.language;
        document.documentElement.translate = false;
        document.documentElement.className = "notranslate";
    }
}

export default observer(App);
